import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";import { motion } from "framer-motion";
import client from "../lib/api";
import { useAuth } from "../lib/auth";
import { XPRing, ProgressBar } from "../components/Progress";
import NotificationBell from "../components/NotificationBell";
import DailyCard from "../components/DailyCard";
import WeeklyCard from "../components/WeeklyCard";
import StreakTokenBanner from "../components/StreakTokenBanner";
import ActiveQuests from "../components/ActiveQuests";
import { Flame, Trophy, Clock, ChevronRight, LogOut, Activity, Zap, Skull, Shield, Coins, Footprints } from "lucide-react";

export default function Dashboard() {
    const { user, logout } = useAuth();
    const [data, setData] = useState(null);

    useEffect(() => {
        client.get("/users/me/dashboard").then((r) => setData(r.data));
    }, []);

    const reload = () => {
        client.get("/users/me/dashboard").then((r) => setData(r.data));
    };

    if (!data || !user) return <div className="p-6 text-zinc-400">Loading the grind...</div>;

    const xpForLevel = data.next_level_xp || 500;
    const greet = new Date().getHours() < 12 ? "Morning" : new Date().getHours() < 18 ? "Afternoon" : "Evening";

    return (
        <div className="px-5 pt-8">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <div className="label-cap text-xs text-zinc-500">Good {greet}</div>
                    <div className="font-display font-black text-3xl uppercase tracking-tight" data-testid="dashboard-name">
                        {user.name}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Link
                        to="/app/shop"
                        data-testid="dashboard-noex-balance"
                        className="surface-2 px-2.5 py-1.5 flex items-center gap-1 text-neon-green hover:border-neon-green transition-colors"
                        title="$NoEx balance — tap to visit Shop"
                    >
                        <Coins size={14} />
                        <span className="font-display font-black text-sm leading-none">
                            {(data.noex_balance ?? 0).toLocaleString()}
                        </span>
                    </Link>
                    <NotificationBell />
                    <button
                        onClick={logout}
                        data-testid="dashboard-logout"
                        className="w-10 h-10 surface-2 flex items-center justify-center hover:border-neon-orange"
                    >
                        <LogOut size={18} className="text-zinc-300" />
                    </button>
                </div>
            </div>

            {/* Hero card with XP ring */}
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-6 surface-2 p-5 relative overflow-hidden"
            >
                <div className="absolute -right-8 -top-8 w-40 h-40 bg-neon-orange/10 rounded-full blur-3xl" />
                <div className="flex items-center gap-5 relative">
                    <XPRing
                        value={user.xp}
                        max={xpForLevel}
                        size={110}
                        color="#39FF14"
                        label={`/ ${xpForLevel} XP`}
                    />
                    <div className="flex-1">
                        <div className="label-cap text-xs text-neon-green">Level {user.current_level}</div>
                        <div className="font-display font-black text-4xl uppercase mt-1 leading-none">
                            {user.current_level >= 5 ? "Beast" : user.current_level >= 3 ? "Warrior" : "Rookie"}
                        </div>
                        <div className="flex items-center gap-3 mt-3 text-sm">
                            <div className="flex items-center gap-1 text-neon-orange">
                                <Flame size={14} />
                                <span className="font-bold">{user.streak} day streak</span>
                            </div>
                        </div>
                        <div className="flex items-center gap-1 mt-2 text-zinc-400 text-xs">
                            <Trophy size={12} className="text-neon-green" />
                            <span>
                                Rank #{data.rank} of {data.total_users}
                            </span>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* Stats grid */}
            <div className="grid grid-cols-3 gap-2 mt-4">
                <StatTile icon={Activity} label="Approved" value={data.approved_count} color="#39FF14" />
                <StatTile icon={Clock} label="Pending" value={data.pending_reviews} color="#F59E0B" />
                <StatTile icon={Zap} label="Badges" value={(user.badges || []).length} color="#FF3B30" />
            </div>

            {/* Quick Run CTA */}
            <Link
                to="/app/runs"
                data-testid="dashboard-run-cta"
                className="mt-3 surface-2 p-3 flex items-center gap-3 border-l-2 border-l-neon-green hover:translate-x-0.5 transition-transform"
            >
                <div className="w-10 h-10 rounded bg-neon-green/15 flex items-center justify-center">
                    <Footprints size={18} className="text-neon-green" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="label-cap text-[10px] text-neon-green">Cardio</div>
                    <div className="font-display font-bold uppercase tracking-tight text-sm">
                        Track a Run or Walk
                    </div>
                </div>
                <ChevronRight size={16} className="text-zinc-500" />
            </Link>

            {/* Streak Protection */}
            <StreakTokenBanner
                tokens={data.streak_tokens || 0}
                atRisk={!!data.streak_at_risk}
                streak={user.streak}
                onChange={reload}
            />

            {/* Time-Sensitive Quests (admin-broadcast) */}
            <ActiveQuests onChange={reload} />

            {/* Daily Challenge */}
            <div className="mt-4">
                <DailyCard onChange={reload} />
            </div>

            {/* Weekly Elimination */}
            <div className="mt-4">
                <WeeklyCard />
            </div>

            {/* Monthly Prove Yourself Challenge */}
            {data.monthly_challenge && (
                <Link
                    to={`/app/challenges/${data.monthly_challenge.id}`}
                    data-testid="monthly-challenge-card"
                    className="mt-4 block relative overflow-hidden border-2 border-neon-orange p-4 hover:scale-[1.01] transition-transform"
                    style={{
                        background: "linear-gradient(135deg, #1a0606 0%, #0d0d0d 60%)",
                        boxShadow: "0 0 32px rgba(255,59,48,0.25), inset 0 0 0 1px rgba(255,59,48,0.4)",
                    }}
                >
                    <div className="absolute -right-6 -top-6 w-32 h-32 bg-neon-orange/20 rounded-full blur-3xl" />
                    <div className="relative flex items-center gap-4">
                        <div className="w-14 h-14 bg-neon-orange/20 border border-neon-orange flex items-center justify-center">
                            <Skull size={28} className="text-neon-orange" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="label-cap text-[10px] text-neon-orange animate-pulse">Monthly · Elite</div>
                            <div className="font-display font-black text-xl uppercase tracking-tight leading-none">
                                {data.monthly_challenge.title}
                            </div>
                            <div className="flex items-center gap-3 mt-2 text-xs">
                                <span className="text-zinc-400">
                                    {data.monthly_challenge.reps_required} reps · {data.monthly_challenge.time_limit_seconds}s
                                </span>
                                <span className="text-neon-green font-bold uppercase tracking-wider">
                                    +{data.monthly_challenge.xp_reward} XP
                                </span>
                            </div>
                        </div>
                        <ChevronRight size={20} className="text-neon-orange" />
                    </div>
                </Link>
            )}

            {/* Today's Challenges */}
            <div className="mt-8">
                <div className="flex items-end justify-between mb-3">
                    <div>
                        <span className="label-cap text-xs text-zinc-500">Today</span>
                        <h2 className="font-display font-black text-2xl uppercase tracking-tight">
                            Your Mission
                        </h2>
                    </div>
                    <Link to="/app/challenges" className="text-zinc-400 text-xs label-cap hover:text-white">
                        See All
                    </Link>
                </div>
                <div className="space-y-3">
                    {data.today_challenges.length === 0 ? (
                        <div className="surface-1 p-6 text-center text-zinc-400">
                            No active challenges. Visit the Challenges tab.
                        </div>
                    ) : (
                        data.today_challenges.map((c, i) => (
                            <motion.div
                                key={c.id}
                                initial={{ opacity: 0, x: 30 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: i * 0.08 }}
                            >
                                <Link
                                    to={`/app/challenges/${c.id}`}
                                    data-testid={`today-challenge-${c.id}`}
                                    className="surface-2 p-4 flex items-center gap-4 hover:border-neon-green transition-colors"
                                >
                                    <div
                                        className="w-14 h-14 flex flex-col items-center justify-center"
                                        style={{
                                            background:
                                                c.difficulty === "elite"
                                                    ? "#FF242420"
                                                    : c.difficulty === "hard"
                                                      ? "#FF3B3020"
                                                      : "#39FF1420",
                                            border: `1px solid ${
                                                c.difficulty === "elite"
                                                    ? "#FF2424"
                                                    : c.difficulty === "hard"
                                                      ? "#FF3B30"
                                                      : "#39FF14"
                                            }`,
                                        }}
                                    >
                                        <span className="font-display font-black text-lg leading-none">{c.reps_required}</span>
                                        <span className="text-[9px] uppercase tracking-wider text-zinc-400">reps</span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-display font-bold uppercase tracking-tight text-base leading-none">
                                            {c.title}
                                        </div>
                                        <div className="flex items-center gap-3 mt-2 text-xs text-zinc-400">
                                            <span className="flex items-center gap-1">
                                                <Clock size={11} /> {c.time_limit_seconds}s
                                            </span>
                                            <span className="uppercase tracking-wider text-neon-green">
                                                +{c.xp_reward} XP
                                            </span>
                                        </div>
                                    </div>
                                    <ChevronRight size={18} className="text-zinc-500" />
                                </Link>
                            </motion.div>
                        ))
                    )}
                </div>
            </div>

            {/* Quick links */}
            <div className="mt-8 grid grid-cols-2 gap-3">
                <Link
                    to="/app/leaderboard"
                    data-testid="link-leaderboard"
                    className="surface-1 p-4 hover:border-neon-orange transition-colors"
                >
                    <Trophy className="text-neon-orange mb-2" size={20} />
                    <div className="font-display font-bold uppercase">Leaderboard</div>
                    <div className="text-xs text-zinc-500 mt-1">See who's ahead</div>
                </Link>
                <Link
                    to="/app/badges"
                    data-testid="link-badges"
                    className="surface-1 p-4 hover:border-neon-green transition-colors"
                >
                    <Zap className="text-neon-green mb-2" size={20} />
                    <div className="font-display font-bold uppercase">Badges</div>
                    <div className="text-xs text-zinc-500 mt-1">Trophies earned</div>
                </Link>
                <Link
                    to="/app/exercises"
                    data-testid="link-library"
                    className="surface-1 p-4 hover:border-neon-orange transition-colors col-span-1"
                >
                    <Activity className="text-neon-orange mb-2" size={20} />
                    <div className="font-display font-bold uppercase">Library</div>
                    <div className="text-xs text-zinc-500 mt-1">Form & technique</div>
                </Link>
                <Link
                    to="/app/workout-plan"
                    data-testid="link-plan"
                    className="surface-1 p-4 hover:border-neon-green transition-colors"
                >
                    <Flame className="text-neon-green mb-2" size={20} />
                    <div className="font-display font-bold uppercase">Weekly Plan</div>
                    <div className="text-xs text-zinc-500 mt-1">Schedule grind</div>
                </Link>
                <Link
                    to="/app/survivors"
                    data-testid="link-survivors"
                    className="surface-1 p-4 hover:border-neon-orange transition-colors col-span-2"
                >
                    <Shield className="text-neon-orange mb-2" size={20} />
                    <div className="font-display font-bold uppercase">Survivors Wall</div>
                    <div className="text-xs text-zinc-500 mt-1">Last week's eliminations</div>
                </Link>
            </div>

            {/* Disclaimer */}
            <div className="mt-8 surface-1 p-4 border-l-2 border-l-neon-orange">
                <div className="label-cap text-xs text-neon-orange">Train Smart</div>
                <p className="text-xs text-zinc-400 mt-2 leading-relaxed">
                    Warm up first. Stop if you feel pain. Hydrate. Consult a doctor if you have medical conditions.
                    No fake progress, but no broken bodies either.
                </p>
            </div>
        </div>
    );
}

function StatTile({ icon: Icon, label, value, color }) {
    return (
        <div className="surface-1 p-3">
            <Icon size={16} style={{ color }} />
            <div className="font-display font-black text-2xl mt-1" style={{ color }}>
                {value}
            </div>
            <div className="label-cap text-[10px] text-zinc-500">{label}</div>
        </div>
    );
}
